import React from "react";
import { Row, Col, Checkbox, Switch } from "antd";
import { Helmet } from "react-helmet";

import { Button } from "shared/components/Button";
import Container from "shared/components/Container";
import { Input, TextArea } from "shared/components/Form";
import { BackLink } from "shared/components/Links";
import LoaderContainer, { BigLoadingSpinner } from "shared/components/LoaderContainer";
import PageTitle from "shared/components/PageTitle";

interface CopilotSettingsProps {
  backLink: { to: string; children: string };
  config: any;
  isLoading: boolean;
  isSaving: boolean;
  onSubmit: (values: any) => void;
}

const title = "Copilot Settings";

const CopilotSettings = ({
  backLink,
  config,
  isLoading,
  isSaving,
  onSubmit,
}: CopilotSettingsProps) => {
  const [formData, setFormData] = React.useState(config || {
    systemPrompt: "",
    responseFormatTemplate: "",
    modelId: "",
    maxTokens: 1000,
    temperature: 0.7,
    rateLimitMessagesPerUser: 100,
    includeLumenData: true,
    includeCreditReports: true,
    includeCriminalBackground: true,
    includeIncomeDetails: true,
  });

  React.useEffect(() => {
    if (config) {
      const newFormData = {
        systemPrompt: config.systemPrompt || "",
        responseFormatTemplate: config.responseFormatTemplate || "",
        modelId: config.modelId || "",
        maxTokens: config.maxTokens || 1000,
        temperature: config.temperature || 0.7,
        rateLimitMessagesPerUser: config.rateLimitMessagesPerUser || 100,
        includeLumenData: config.includeLumenData ?? true,
        includeCreditReports: config.includeCreditReports ?? true,
        includeCriminalBackground: config.includeCriminalBackground ?? true,
        includeIncomeDetails: config.includeIncomeDetails ?? true,
      };
      setFormData(newFormData);
    }
  }, [config]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      system_prompt: formData.systemPrompt,
      response_format_template: formData.responseFormatTemplate,
      model_id: formData.modelId,
      max_tokens: formData.maxTokens,
      temperature: formData.temperature,
      rate_limit_messages_per_user: formData.rateLimitMessagesPerUser,
      include_lumen_data: formData.includeLumenData,
      include_credit_reports: formData.includeCreditReports,
      include_criminal_background: formData.includeCriminalBackground,
      include_income_details: formData.includeIncomeDetails,
    };
    onSubmit(payload);
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container noPaddingBottom expand>
        <BackLink.Container>
          <BackLink to={backLink.to}>{backLink.children}</BackLink>
        </BackLink.Container>
        <PageTitle.Container>
          <PageTitle>{title}</PageTitle>
        </PageTitle.Container>

        {isLoading ? (
          <LoaderContainer
            Loader={<BigLoadingSpinner />}
            fetching
          />
        ) : (
          <form onSubmit={handleSubmit} style={{ maxWidth: 900 }}>
            {/* Prompts Section */}
            <div style={{ marginBottom: 48 }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a'
              }}>
                Prompts
              </h3>
              <p style={{
                fontSize: 14,
                color: '#666',
                marginBottom: 24,
                lineHeight: 1.5
              }}>
                Customize how the AI assistant behaves and formats responses
              </p>

              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                  color: '#333'
                }}>
                  System Prompt
                </label>
                <p style={{
                  fontSize: 12,
                  color: '#888',
                  marginBottom: 8,
                  lineHeight: 1.4
                }}>
                  Define the AI's role, behavior, and data interpretation rules. Leave empty to use defaults.
                </p>
                <TextArea
                  id="systemPrompt"
                  name="systemPrompt"
                  placeholder="You are a helpful assistant for property managers..."
                  value={formData.systemPrompt}
                  onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                  rows={6}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 13,
                    lineHeight: 1.5
                  }}
                />
              </div>

              <div style={{ marginBottom: 0 }}>
                <label style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                  color: '#333'
                }}>
                  Response Format Template
                </label>
                <p style={{
                  fontSize: 12,
                  color: '#888',
                  marginBottom: 8,
                  lineHeight: 1.4
                }}>
                  Instructions for how to structure responses (e.g., use bullet points, include risk scores)
                </p>
                <TextArea
                  id="responseFormatTemplate"
                  name="responseFormatTemplate"
                  placeholder="Always respond in bullet points with Risk/Recommendation sections..."
                  value={formData.responseFormatTemplate}
                  onChange={(e) => setFormData({ ...formData, responseFormatTemplate: e.target.value })}
                  rows={4}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 13,
                    lineHeight: 1.5
                  }}
                />
              </div>
            </div>

            {/* Model Settings Section */}
            <div style={{ marginBottom: 48 }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a'
              }}>
                Model Configuration
              </h3>
              <p style={{
                fontSize: 14,
                color: '#666',
                marginBottom: 24,
                lineHeight: 1.5
              }}>
                Configure the AI model and generation parameters
              </p>

              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <label style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 8,
                    color: '#333'
                  }}>
                    Model ID
                  </label>
                  <p style={{
                    fontSize: 12,
                    color: '#888',
                    marginBottom: 8,
                    lineHeight: 1.4
                  }}>
                    AWS Bedrock model identifier. Leave empty to use Claude 3.5 Sonnet.
                  </p>
                  <Input
                    id="modelId"
                    name="modelId"
                    placeholder="us.anthropic.claude-sonnet-4-5-20250929-v1:0"
                    value={formData.modelId}
                    onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 13 }}
                  />
                </Col>

                <Col span={12}>
                  <label style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 8,
                    color: '#333'
                  }}>
                    Max Output Tokens
                  </label>
                  <p style={{
                    fontSize: 12,
                    color: '#888',
                    marginBottom: 12,
                    lineHeight: 1.4
                  }}>
                    Maximum length of AI responses (~750 words per 1000 tokens)
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <input
                      type="range"
                      min={100}
                      max={10000}
                      step={100}
                      value={formData.maxTokens}
                      onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) })}
                      style={{ flex: 1 }}
                    />
                    <span style={{
                      fontSize: 14,
                      fontWeight: 500,
                      minWidth: 60,
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      {formData.maxTokens}
                    </span>
                  </div>
                </Col>

                <Col span={12}>
                  <label style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 8,
                    color: '#333'
                  }}>
                    Temperature
                  </label>
                  <p style={{
                    fontSize: 12,
                    color: '#888',
                    marginBottom: 12,
                    lineHeight: 1.4
                  }}>
                    Controls randomness. Lower = more focused, Higher = more creative
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={formData.temperature}
                      onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                      style={{ flex: 1 }}
                    />
                    <span style={{
                      fontSize: 14,
                      fontWeight: 500,
                      minWidth: 60,
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      {formData.temperature.toFixed(1)}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Rate Limiting Section */}
            <div style={{ marginBottom: 48 }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a'
              }}>
                Rate Limiting
              </h3>
              <p style={{
                fontSize: 14,
                color: '#666',
                marginBottom: 24,
                lineHeight: 1.5
              }}>
                Control usage limits to manage costs
              </p>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                  color: '#333'
                }}>
                  Messages Per User Per Day
                </label>
                <p style={{
                  fontSize: 12,
                  color: '#888',
                  marginBottom: 12,
                  lineHeight: 1.4
                }}>
                  Maximum number of copilot messages each user can send daily
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, maxWidth: 500 }}>
                  <input
                    type="range"
                    min={1}
                    max={1000}
                    step={10}
                    value={formData.rateLimitMessagesPerUser}
                    onChange={(e) => setFormData({ ...formData, rateLimitMessagesPerUser: parseInt(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <span style={{
                    fontSize: 14,
                    fontWeight: 500,
                    minWidth: 60,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                    {formData.rateLimitMessagesPerUser}
                  </span>
                </div>
              </div>
            </div>

            {/* Context Control Section */}
            <div style={{ marginBottom: 48 }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a'
              }}>
                Context Control
              </h3>
              <p style={{
                fontSize: 14,
                color: '#666',
                marginBottom: 24,
                lineHeight: 1.5
              }}>
                Choose what data is sent to the AI for analysis
              </p>

              <div style={{
                background: '#fef3cd',
                border: '1px solid #f9e79f',
                borderRadius: 6,
                padding: '12px 16px',
                marginBottom: 24,
                fontSize: 13,
                color: '#856404',
                lineHeight: 1.5
              }}>
                Disabling data types will prevent them from being included in AI requests
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                {[
                  { key: 'includeLumenData', label: 'Lumen Scoring Data' },
                  { key: 'includeCreditReports', label: 'Credit Reports' },
                  { key: 'includeCriminalBackground', label: 'Criminal Background' },
                  { key: 'includeIncomeDetails', label: 'Income & Employment' },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    style={{
                      padding: '16px',
                      border: '1px solid #e5e5e5',
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      background: formData[key] ? '#fff' : '#f9f9f9',
                    }}
                    onClick={() => setFormData({ ...formData, [key]: !formData[key] })}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
                        {label}
                      </span>
                      <Switch
                        checked={formData[key]}
                        size="small"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div style={{
              borderTop: '1px solid #e5e5e5',
              paddingTop: 24,
              display: 'flex',
              gap: 12
            }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSaving}
                style={{ minWidth: 140 }}
              >
                Save Configuration
              </Button>
              <Button
                onClick={() => window.location.href = backLink.to}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Container>
    </>
  );
};

export default CopilotSettings;
